import React, { useEffect, useState } from 'react';
import { Translation, getI18n } from 'react-i18next';
import {
  Typography,
  Form,
  Tooltip,
  Input,
  Radio,
  Space,
  Row,
  Col,
  InputNumber,
} from 'antd';
import { withRouter } from 'react-router';

import Logger from '../../../../../lib/Logger';
import notification from '../../../elements/lib/NotificationWrapper';
import LearningAdminModuleFormFooter from './LearningAdminModuleFormFooter';
import ModuleSerieInput from '../containers/ModuleSerieInputContainer';
import { pathTo } from '../../../Routes';
import QueryString from '../../../../../lib/QueryString';
import PublicStatusInput from './PublicStatusInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import ModuleCategoryInput from '../containers/ModuleCategoryInputContainer';
import QuillEditor from './QuillEditor';
import CoverImageUploadModule from './CoverImageUploadModule';
import MediaUploadModule from './MediaUploadModule';
import {
  MixpanelTracker,
  mixpanelEventsEnum,
} from '../../../../../lib/Mixpanel';

const { Title } = Typography;

const layout = {
  main: {
    wrapperCol: { span: 24 },
  },
};

const reFileKey = /(?:(.*?)\/)?(.*?)\.(\w+)/;

const LearningAdminModuleFormStep1 = ({
  id,
  load,
  created_id,
  partnershipId,
  data,
  isSubmitting,
  errors,
  destroyForm,
  isLoading,
  setConfirmationModalOpen,
  isChangeValue,
  modules,
  step,
  setIsChangeValue,
  oldParams,
  setTargetPath,
  nextStep,
  setModuleSerieId,
  history,
  getSignedUrl,
  remove,
  trackerActions,
  partnershipName,
  ...props
}) => {
  const [form] = Form.useForm();

  const title = form.getFieldValue('title');
  const [uploadType, setUploadType] = useState(1);
  const [publicStatus, setPublicStatus] = useState(2);
  const [isContentChange, setContentChange] = useState(false);
  const [uploadedCoverImage, setUploadedCoverImage] = useState(null);
  const [uploadedMedia, setUploadedMedia] = useState(null);

  // load record data from API
  useEffect(() => {
    if (id) {
      load(partnershipId, id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      form.setFieldsValue(dataObj);
      setPublicStatus(dataObj['public_status']);

      if (dataObj['cover_image']) {
        form.setFieldsValue({
          cover_image: 'preloaded',
          cover_image_id: dataObj['cover_image']['id'],
        });

        if (
          dataObj['cover_image'] &&
          dataObj['cover_image']['file_directory'] &&
          dataObj['cover_image']['file_name'] &&
          dataObj['cover_image']['file_extension']
        ) {
          const size = 160;
          const path = `ratio/${size}x${size}/${dataObj['cover_image']['file_directory']}/${dataObj['cover_image']['file_name']}.${dataObj['cover_image']['file_extension']}`;

          getSignedUrl(path, (success, signedUrl) => {
            if (success) {
              const defaultFile = [
                {
                  uid: dataObj['cover_image']['id'],
                  name: dataObj['cover_image']['file_name'],
                  url: signedUrl,
                  thumbUrl: signedUrl,
                  status: 'done',
                },
              ];
              setUploadedCoverImage(defaultFile);
            }
          });
        }
      }

      if (dataObj['media']) {
        setUploadType(1);
        form.setFieldsValue({
          media: 'preloaded',
          media_id: dataObj['media']['id'],
        });

        if (
          dataObj['media'] &&
          dataObj['media']['file_directory'] &&
          dataObj['media']['file_name'] &&
          dataObj['media']['file_extension']
        ) {
          const size = 160;
          const path = `ratio/${size}x${size}/${dataObj['media']['file_directory']}/${dataObj['media']['file_name']}.${dataObj['media']['file_extension']}`;

          getSignedUrl(path, (success, signedUrl) => {
            if (success) {
              const defaultFile = [
                {
                  uid: dataObj['media']['id'],
                  name: dataObj['media']['file_name'],
                  url: signedUrl,
                  thumbUrl: signedUrl,
                  status: 'done',
                },
              ];
              setUploadedCoverImage(defaultFile);
            }
          });
        }
      }

      if (dataObj['video_youtube_url']) {
        setUploadType(2);
      }

      if (dataObj['video_vimeo_url']) {
        setUploadType(3);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setIsChangeValue(false);
      const path = QueryString.append(
        pathTo('LearningsAdminScreen'),
        oldParams
      );
      setTargetPath(path);
    }
    return () => {
      destroyForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created_id, destroyForm]);

  const errorMessage = () => {
    notification.error(
      getI18n().t('resource_form_feedback_image_upload_error')
    );
  };

  const processData = (values, payload) => {
    for (const input of Object.keys(data)) {
      if (input in values) {
        if (input === 'public_status') {
          payload[input] = values[input] ? values[input] : 2;
        } else if (input !== 'cover_image' && input !== 'media') {
          payload[input] = values[input];
        }
      }
    }

    payload['partnership_id'] = partnershipId;

    if (id) {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.LEARNINGS_ADMIN_EDIT_SUBMIT_MODULE
      );

      // update
      props.update(id, payload, (success) => {
        if (success) {
          notification.success(getI18n().t('feedback_form_success'));
          setIsChangeValue(false);
          destroyForm();
          const path = QueryString.append(
            pathTo('LearningsAdminScreen'),
            oldParams
          );
          history.push(path);
        } else {
          notification.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.LEARNINGS_ADMIN_ADD_SUBMIT_MODULE
      );

      props.create(payload, (success) => {
        if (success) {
          notification.success(getI18n().t('feedback_form_success'));
        } else {
          notification.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  const uploadMedia = (values, payload, media) => {
    props.getUploadUrl(
      media.file.name.split('.').pop(),
      'learning-module-media',
      async (success, upload_url, file_id) => {
        if (success) {
          // upload image
          props.upload(upload_url, media.file, async (uploadSuccess) => {
            const directory = 'learning-module-media/';
            file_id = file_id.replace(directory, '');
            if (uploadSuccess) {
              const matches = reFileKey.exec(file_id);
              const updateData = {
                file_directory: directory.slice(0, -1),
                file_name: matches[2],
                file_extension: matches[3],
              };

              props.createImagePartnership(
                partnershipId,
                updateData,
                (createSuccess, imageId) => {
                  if (createSuccess) {
                    payload['media_id'] = imageId;
                    processData(values, payload);
                  } else {
                    errorMessage();
                  }
                }
              );
            } else {
              errorMessage();
            }
          });
        } else {
          errorMessage();
        }
      }
    );
  };

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `LearningAdminModuleFormStep1.submitData(###)`);

    const payload = {};

    const cover_image = values['cover_image'];
    const media = values['media'];

    if (cover_image === 'preloaded') {
      payload['cover_image_id'] = data['cover_image']['id'];

      if (media && media !== 'preloaded') {
        uploadMedia(values, payload, media);
      } else {
        if (media) {
          payload['media_id'] = data['media']['id'];
        }
        processData(values, payload);
      }
    } else {
      props.getUploadUrl(
        cover_image.file.name.split('.').pop(),
        'learning-module-cover-images',
        async (success, upload_url, file_id) => {
          if (success) {
            // upload image
            props.upload(
              upload_url,
              cover_image.file,
              async (uploadSuccess) => {
                const directory = 'learning-module-cover-images/';
                file_id = file_id.replace(directory, '');
                if (uploadSuccess) {
                  const matches = reFileKey.exec(file_id);
                  const updateData = {
                    file_directory: directory.slice(0, -1),
                    file_name: matches[2],
                    file_extension: matches[3],
                  };

                  props.createImagePartnership(
                    partnershipId,
                    updateData,
                    (createSuccess, imageId) => {
                      if (createSuccess) {
                        payload['cover_image_id'] = imageId;

                        if (media && media !== 'preloaded') {
                          uploadMedia(values, payload, media);
                        } else {
                          processData(values, payload);
                        }
                      } else {
                        errorMessage();
                      }
                    }
                  );
                } else {
                  errorMessage();
                }
              }
            );
          } else {
            errorMessage();
          }
        }
      );
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `LearningAdminModuleFormStep1.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `LearningAdminModuleFormStep1.handleFinishFailed(###)`);
    notification.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    for (const key of Object.keys(changedValues)) {
      form.setFields([{ name: key, errors: [] }]);
    }
    setIsChangeValue(true);
  };

  const onClickCancel = () => {
    const page = 'LearningsAdminScreen';
    const path = QueryString.append(pathTo(page), oldParams);

    MixpanelTracker.mixpanelTrack(
      mixpanelEventsEnum.PAGES.ACCESS_PAGE_ADMINISTRATOR,
      {
        partnershipName: partnershipName,
        partnershipId: partnershipId,
        page: page,
      }
    );

    if (isChangeValue) {
      setTargetPath(path);
      setConfirmationModalOpen(true);
    } else {
      history.push(path);
    }
  };

  const handleRemoveModule = () => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.LEARNINGS_ADMIN_DELETE_MODULE
    );
    remove(partnershipId, id, (success) => {
      if (success) {
        setIsChangeValue(false);
        const path = QueryString.append(
          pathTo('LearningsAdminScreen'),
          oldParams
        );
        history.push(path);
        notification.success(getI18n().t('feedback_delete_success'));
      } else {
        notification.error(getI18n().t('feedback_delete_error'));
      }
    });
  };

  return (
    <Translation>
      {(t) => (
        <div className='module-form-step-1'>
          <Form
            name='learning_admin_module_form_step_1'
            form={form}
            initialValues={data}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            onValuesChange={handleValuesChange}
            validateTrigger='onSubmit'
            {...layout.main}
          >
            <Title level={5}>
              {t('learning_admin_form_module_title_step_1')}
            </Title>

            <ModuleSerieInput
              name='module_serie_id'
              rules={[
                {
                  required: true,
                  message: t('feedback_validation_required'),
                },
              ]}
              disabled={isLoading || isSubmitting}
              label='learning_admin_form_input_module_serie'
            />

            <div
              className='ant-collapse ant-collapse-icon-position-left'
              style={{
                backgroundColor: 'white',
                marginTop: '20px',
              }}
            >
              <div className='ant-collapse-item ant-collapse-item-active ant-collapse-no-arrow'>
                <div
                  className='ant-collapse-header'
                  role='button'
                  tabindex='0'
                  aria-expanded='true'
                  style={{
                    cursor: 'auto',
                  }}
                >
                  <span className='ant-typography module-serie-form-step-2-panel-title-text'>
                    {id ? title : 'New Module '}
                  </span>

                  <div className='ant-collapse-extra'>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {id ? (
                        <PublicStatusInput
                          name={'public_status'}
                          disabled={isLoading || isSubmitting}
                        />
                      ) : (
                        <div className='form-group'>
                          <Form.Item
                            name={'public_status'}
                            style={{ display: 'none' }}
                          >
                            <Input type='hidden' />
                          </Form.Item>
                        </div>
                      )}

                      {publicStatus === 3 ? (
                        <FontAwesomeIcon
                          style={{
                            cursor: 'pointer',
                          }}
                          onClick={(event) => handleRemoveModule()}
                          icon={faTrashCan}
                          className='module-action-icon-delete'
                        />
                      ) : (
                        <Tooltip
                          placement='topRight'
                          title={t(
                            'learning_module_series_message_info_delete'
                          )}
                          arrowPointAtCenter
                        >
                          <FontAwesomeIcon
                            style={{ marginTop: '6px' }}
                            icon={faTrashCan}
                            className='module-action-icon-delete-disabled'
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
                <div className='ant-collapse-content ant-collapse-content-active'>
                  <div className='ant-collapse-content-box'>
                    <div style={{ padding: '0px 30px' }}>
                      <div className='form-group'>
                        <div
                          className='ant-col ant-col-24 ant-form-item-label'
                          style={{ paddingBottom: '0' }}
                        >
                          <label className='ant-form-item-required'>
                            {t('learning_admin_form_input_title')}
                          </label>
                        </div>
                        <Form.Item
                          name={'title'}
                          rules={[
                            {
                              required: true,
                              message: t('feedback_validation_required'),
                            },
                            {
                              type: 'string',
                              min: 1,
                              max: 60,
                              message: t('feedback_validation_char_range', {
                                min: 1,
                                max: 60,
                              }),
                            },
                          ]}
                        >
                          <Input disabled={isLoading || isSubmitting} />
                        </Form.Item>
                      </div>

                      <div className='form-group'>
                        <div
                          className='ant-col ant-col-24 ant-form-item-label'
                          style={{ paddingBottom: '0' }}
                        >
                          <label className='ant-form-item-required'>
                            {t('learning_admin_form_input_description')}
                          </label>
                        </div>
                        <Form.Item
                          name={'description'}
                          rules={[
                            {
                              required: true,
                              message: t('feedback_validation_required'),
                            },
                            {
                              type: 'string',
                              min: 1,
                              message: t('feedback_validation_char_range', {
                                min: 1,
                              }),
                            },
                          ]}
                        >
                          <Input.TextArea
                            disabled={isLoading || isSubmitting}
                          />
                        </Form.Item>
                      </div>

                      <CoverImageUploadModule
                        form={form}
                        uploadedCoverImage={uploadedCoverImage}
                        setUploadedCoverImage={setUploadedCoverImage}
                      />
                      <div
                        className='form-group'
                        style={{ marginBottom: '10px' }}
                      >
                        <div
                          className='ant-col ant-col-24 ant-form-item-label'
                          style={{ paddingBottom: '0' }}
                        >
                          <label className='ant-form-item-required'>
                            {t('learning_admin_form_input_upload_type')}
                          </label>
                        </div>

                        <Radio.Group
                          size='large'
                          onChange={(e) => setUploadType(e.target.value)}
                          defaultValue={1}
                          value={uploadType}
                        >
                          <Space direction='horizontal'>
                            <Radio value={1}>
                              {t('learning_admin_form_input_upload_type_1')}
                            </Radio>
                            <Radio value={2}>
                              {t('learning_admin_form_input_upload_type_2')}
                            </Radio>
                            <Radio value={3}>
                              {t('learning_admin_form_input_upload_type_3')}
                            </Radio>
                          </Space>
                        </Radio.Group>
                      </div>
                      {uploadType === 1 && (
                        <MediaUploadModule
                          form={form}
                          uploadedMedia={uploadedMedia}
                          setUploadedMedia={setUploadedMedia}
                        />
                      )}

                      {uploadType === 2 && (
                        <div className='form-group'>
                          <div
                            className='ant-col ant-col-24 ant-form-item-label'
                            style={{ paddingBottom: '0' }}
                          >
                            <label className='ant-form-item'>
                              {t('learning_admin_form_input_video_youtube_url')}
                            </label>
                          </div>
                          <Form.Item
                            name={'video_youtube_url'}
                            rules={[
                              {
                                required: true,
                                message: t('feedback_validation_required'),
                              },
                              {
                                pattern:
                                  /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(?:-nocookie)?\.com|youtu\.be))(\/(?:[\w-]+\?v=|embed\/|live\/|v\/)?)([\w-]+)(\S+)?$/,
                                message: t('feedback_validation_url'),
                              },
                            ]}
                          >
                            <Input disabled={isLoading || isSubmitting} />
                          </Form.Item>
                        </div>
                      )}

                      {uploadType === 3 && (
                        <div className='form-group'>
                          <div
                            className='ant-col ant-col-24 ant-form-item-label'
                            style={{ paddingBottom: '0' }}
                          >
                            <label className='ant-form-item'>
                              {t('learning_admin_form_input_video_vimeo_url')}
                            </label>
                          </div>
                          <Form.Item
                            name={'video_vimeo_url'}
                            rules={[
                              {
                                required: true,
                                message: t('feedback_validation_required'),
                              },
                              {
                                pattern: new RegExp(
                                  '^(?:https?:\\/\\/)?(?:www\\.)?(?:player\\.)?vimeo\\.com\\/(?:channels\\/(?:\\w+\\/)?|groups\\/[^/]*\\/videos\\/|video\\/)?(\\d+)(?:|/?)?$'
                                ),
                                message: t('feedback_validation_url'),
                              },
                            ]}
                          >
                            <Input disabled={isLoading || isSubmitting} />
                          </Form.Item>
                        </div>
                      )}

                      <Row gutter={48}>
                        <Col
                          xs={{ span: 24, order: 1 }}
                          sm={{ span: 12, order: 1 }}
                        >
                          <div className='form-group'>
                            <div
                              className='ant-col ant-col-24 ant-form-item-label'
                              style={{ paddingBottom: '0' }}
                            >
                              <label className='ant-form-item-required'>
                                {t('learning_admin_form_input_order')}
                              </label>
                            </div>
                            <Form.Item
                              name={'order'}
                              rules={[
                                {
                                  required: true,
                                  message: t('feedback_validation_required'),
                                },
                                {
                                  type: 'number',
                                  message: t('feedback_validation_number'),
                                },
                                {
                                  type: 'number',
                                  min: 0,
                                  message: t('feedback_validation_range', {
                                    min: 0,
                                  }),
                                },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                disabled={isLoading || isSubmitting}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </div>
                        </Col>

                        <Col
                          xs={{ span: 24, order: 2 }}
                          sm={{ span: 12, order: 2 }}
                        >
                          <div className='form-group'>
                            <div
                              className='ant-col ant-col-24 ant-form-item-label'
                              style={{ paddingBottom: '0' }}
                            >
                              <label className='ant-form-item'>
                                {t('learning_admin_form_input_assign_category')}
                              </label>
                            </div>
                            <ModuleCategoryInput
                              name={'categories_ids'}
                              disabled={isLoading || isSubmitting}
                              form={form}
                              trackerActions={trackerActions}
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className='form-group'>
                        <div
                          className='ant-col ant-col-24 ant-form-item-label'
                          style={{ paddingBottom: '0' }}
                        >
                          <label className='ant-form-item-required'>
                            {t('learning_admin_form_input_content')}
                          </label>
                        </div>
                        <Form.Item
                          name={'content'}
                          rules={[
                            {
                              required: true,
                              message: t('feedback_validation_required'),
                            },
                          ]}
                        >
                          <QuillEditor
                            disabled={isLoading || isSubmitting}
                            isContentChange={isContentChange}
                            setIsChangeValue={setIsChangeValue}
                            setContentChange={setContentChange}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <LearningAdminModuleFormFooter
              id={id}
              disabled={isLoading || isSubmitting}
              module={modules[step]}
              onClickCancel={onClickCancel}
            />
          </Form>
        </div>
      )}
    </Translation>
  );
};

export default withRouter(LearningAdminModuleFormStep1);
